@if (doc) {
<div>
    <div class="d-flex align-items-center gap-3 mt-3 mb-4">
        <i class="ri {{ getMimeTypeIcon(doc.mimetype) }} ri-2x text-secondary"></i>
        <h3 class="mb-0">{{ doc.filename }}</h3>
    </div>

    <table class="table table-sm table-borderless mb-4">
        <tbody>
            <tr>
                <td
                    class="text-muted"
                    style="width: 40%;"
                    i18n="@@upload-date"
                >Hochgeladen am</td>
                <td>{{ dayjs(doc.uploadDate).format('DD.MM.YYYY, HH:mm') }}</td>
            </tr>
            <tr>
                <td
                    class="text-muted"
                    i18n="@@uploaded-by"
                >Hochgeladen von</td>
                <td>{{ doc.uploadedBy }}</td>
            </tr>
            @if (!doc.mimetype.startsWith('image/')) {
            <tr>
                <td
                    class="text-muted"
                    i18n="@@chunks"
                >Chunks</td>
                <td>{{ doc.chunkCount }}</td>
            </tr>
            }
        </tbody>
    </table>

    @if (doc.extractionStatus && !doc.mimetype.startsWith('image/')) {
    <div class="mb-3">
        <h6
            class="text-muted mb-2"
            i18n="@@extraction-status-label"
        >Extraktionsstatus</h6>
        @if (doc.extractionStatus === 'extracted') {
        <p-tag
            severity="success"
            value="Extrahiert"
            i18n-value="@@extraction-status-extracted"
        />
        }
        @if (doc.extractionStatus === 'uploaded') {
        <p-tag
            severity="warn"
            value="Noch nicht extrahiert"
            i18n-value="@@extraction-status-uploaded"
        />
        }
        @if (doc.extractionStatus === 'extracting') {
        <p-tag
            severity="info"
            value="Wird extrahiert…"
            i18n-value="@@extraction-status-extracting"
        />
        }
        @if (doc.extractionStatus === 'failed') {
        <p-tag
            severity="danger"
            value="Extraktion fehlgeschlagen"
            i18n-value="@@extraction-status-failed"
        />
        }
    </div>
    }

    @if (doc.extractionConfig && !doc.mimetype.startsWith('image/')) {
    <div class="mb-4">
        <h6
            class="text-muted mb-2"
            i18n="@@extraction-config-label"
        >Extraktionskonfiguration</h6>
        <table class="table table-sm table-borderless mb-0">
            <tbody>
                <tr>
                    <td
                        class="text-muted"
                        style="width: 40%;"
                        i18n="@@extraction-max-chars-label"
                    >Abschnittsgröße</td>
                    <td>{{ doc.extractionConfig.maxChars }}</td>
                </tr>
                <tr>
                    <td
                        class="text-muted"
                        i18n="@@extraction-max-overlap-label"
                    >Überschneidung</td>
                    <td>{{ doc.extractionConfig.maxOverlap }}</td>
                </tr>
                <tr>
                    <td
                        class="text-muted"
                        i18n="@@extraction-chunking-strategy"
                    >Ergebnisstruktur</td>
                    <td>{{ doc.extractionConfig.chunking_strategy }}</td>
                </tr>
                <tr>
                    <td
                        class="text-muted"
                        i18n="@@extraction-images-enabled"
                    >Bilder analysieren</td>
                    <td>
                        @if (doc.extractionConfig.imagesEnabled) {
                        <i class="ri-check-line text-success"></i>
                        } @else {
                        <i class="ri-close-line text-danger"></i>
                        }
                    </td>
                </tr>
                <tr>
                    <td
                        class="text-muted"
                        i18n="@@extraction-ocr-enabled"
                    >OCR aktivieren</td>
                    <td>
                        @if (doc.extractionConfig.ocrEnabled) {
                        <span>{{ doc.extractionConfig.ocrLanguage }}</span>
                        } @else {
                        <i class="ri-close-line text-danger"></i>
                        }
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
    }

    @if (doc.tableOfContents && doc.tableOfContents.length > 0) {
    <div class="mb-4">
        <h6
            class="text-muted mb-2"
            i18n="@@table-of-contents-label"
        >Erkannte Kapitelstruktur</h6>
        <ul class="list-unstyled mb-0">
            @for (entry of doc.tableOfContents; track $index) {
            <li [style.paddingLeft.rem]="(entry.level - 1) * 1">
                {{ entry.title }}
                @if (entry.page !== null) {
                <span class="text-muted"> (Seite {{ entry.page }})</span>
                }
            </li>
            }
        </ul>
    </div>
    }

    @if (doc.usedIn.length > 0) {
    <div class="mb-4">
        <h6
            class="text-muted mb-2"
            i18n="@@used-in-bots"
        >Verwendet in</h6>
        <ul class="list-unstyled mb-0">
            @for (usage of doc.usedIn; track usage.botId) {
            <li>
                <a
                    class="href-muted"
                    [routerLink]="['/bot-admin', usage.botId, 'documents']"
                >{{ usage.botName }}</a>
            </li>
            }
        </ul>
    </div>
    }

    @if (!doc.mimetype.startsWith('image/')) {
    <p-inputgroup class="mb-2">
        <input
            pInputText
            #searchInput
            [disabled]="searchIsRunning"
            placeholder="Suchanfrage eingeben…"
            i18n-placeholder="@@search-placeholder-doc"
            fluid
        />
        <p-button
            severity="primary"
            [style]="{ 'border-top-left-radius': '0', 'border-bottom-left-radius': '0' }"
            (onClick)="runSearchTest(searchInput.value)"
            [disabled]="searchIsRunning"
            [loading]="searchIsRunning"
            label="Suchanfrage testen"
            i18n-label="@@test-search-phrase"
        />
    </p-inputgroup>

    @if (searchCompleted && searchResults.length === 0) {
    <p-message
        severity="warn"
        [closable]="false"
        class="mb-3 d-block"
    >
        <span i18n="@@no-search-results">Keine Ergebnisse gefunden.</span>
    </p-message>
    }

    @if (searchCompleted && searchResults.length > 0) {
    <p-table
        [value]="searchResults"
        [rowHover]="true"
        styleClass="p-datatable-sm mb-3"
    >
        <ng-template pTemplate="header">
            <tr>
                <th>Score</th>
                <th i18n="@@pages">Seiten</th>
                <th i18n="@@content">Inhalt</th>
            </tr>
        </ng-template>
        <ng-template
            pTemplate="body"
            let-result
        >
            <tr>
                <td>{{ result.chunk.vs_score * 100 | number:"2.2-2" }}%</td>
                <td>
                    @if (result.chunk.first_page === result.chunk.last_page) {
                    {{ result.chunk.first_page }}
                    } @else {
                    {{ result.chunk.first_page }} – {{ result.chunk.last_page }}
                    }
                </td>
                <td>{{ result.chunk.content }}</td>
            </tr>
        </ng-template>
    </p-table>
    }

    }

    @if (!doc.mimetype.startsWith('image/')) {
    <p-accordion
        class="mt-3"
        [value]="['0']"
    >
        <p-accordion-panel value="0">
            <p-accordion-header>
                <i class="ri-file-text-fill me-2"></i>
                <span i18n="@@document-view"><b>textbasierte Dokumentenvorschau (KI-optimiert)</b></span>
            </p-accordion-header>
            <p-accordion-content>
                @if (chunksLoading()) {
                <div class="text-center p-4">
                    <i class="ri-loader-2-line spinning-icon ri-xl text-muted"></i>
                </div>
                } @else if (chunks().length === 0) {
                <p
                    class="text-muted text-center p-3 mb-0"
                    i18n="@@no-chunks"
                >Keine Chunks vorhanden. Das Dokument wurde noch nicht extrahiert.</p>
                } @else {
                <p-table
                    [value]="chunks()"
                    [rowHover]="true"
                    styleClass="p-datatable-sm"
                >
                    <ng-template pTemplate="header">
                        <tr>
                            <th i18n="@@pages">Seiten</th>
                            <th i18n="@@content">Inhalt</th>
                        </tr>
                    </ng-template>
                    <ng-template
                        pTemplate="body"
                        let-chunk
                    >
                        <tr>
                            <td>
                                @if (chunk.first_page === chunk.last_page) {
                                {{ chunk.first_page }}
                                } @else {
                                {{ chunk.first_page }} – {{ chunk.last_page }}
                                }
                            </td>
                            <td>{{ chunk.content }}</td>
                        </tr>
                    </ng-template>
                </p-table>
                }
            </p-accordion-content>
        </p-accordion-panel>
    </p-accordion>
    }
</div>
}